import { useState, useEffect, useRef, useCallback } from 'react';
import { getAccessToken } from '../api/client';

// ============================================================
// Types
// ============================================================
interface PosicionDebCred {
  totalIva: number;
  iva21: number;
  iva105: number;
  total: number;
  neto: number;
  count: number;
}
interface EmitidoFecha {
  fecha: string;
  iva21: number;
  iva105: number;
  totalIva: number;
  total: number;
  neto: number;
  count: number;
}
interface RecibidoEmisor {
  emisor: string;
  iva21: number;
  iva105: number;
  totalIva: number;
  total: number;
  count: number;
  ultimaFecha: string;
}
interface Posicion {
  periodo: string;
  debito: PosicionDebCred;
  credito: PosicionDebCred;
  posicion: number;
  tipo: 'a_pagar' | 'a_favor' | 'equilibrio';
  emitidosPorFecha: EmitidoFecha[];
  recibidosPorEmisor: RecibidoEmisor[];
  periodos: string[];
}

// ============================================================
// Helpers
// ============================================================
function fmt(n: number) {
  return (
    '$ ' +
    Math.round(Math.abs(n)).toLocaleString('es-AR')
  );
}
function fmtSigned(n: number) {
  return (n < 0 ? '-' : '') + fmt(n);
}

const API = (import.meta.env.VITE_API_URL ?? '') + '/api/v3';

// ============================================================
// Manual entry form state
// ============================================================
interface ManualForm {
  lado: 'emitido' | 'recibido';
  tipoLetra: 'A' | 'B' | 'C' | 'M';
  esNc: boolean;
  fecha: string;
  denominacion: string;
  neto: string;
  alicuota: string;
  periodo: string;
  notas: string;
}

const defaultForm = (periodo: string): ManualForm => ({
  lado: 'recibido',
  tipoLetra: 'A',
  esNc: false,
  fecha: new Date().toISOString().slice(0, 10),
  denominacion: '',
  neto: '',
  alicuota: '0.21',
  periodo,
  notas: '',
});

// ============================================================
// Main page
// ============================================================
export function IvaPage() {
  const token = getAccessToken();
  const [periodo, setPeriodo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [posicion, setPosicion] = useState<Posicion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload state
  const [uploadLado, setUploadLado] = useState<'emitido' | 'recibido'>('emitido');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPeriodo, setUploadPeriodo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manual form
  const [form, setForm] = useState<ManualForm>(defaultForm(periodo));
  const [savingManual, setSavingManual] = useState(false);
  const [manualMsg, setManualMsg] = useState('');

  // Tabs
  const [activeTab, setActiveTab] = useState<'posicion' | 'emitidos' | 'recibidos' | 'comprobantes'>('posicion');

  // ----------------------------------------------------------------
  const fetchPosicion = useCallback(async (p: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/iva/posicion?periodo=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setPosicion(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPosicion(periodo); }, [periodo, fetchPosicion]);

  // ----------------------------------------------------------------
  // Upload CSV
  // ----------------------------------------------------------------
  const handleUpload = async () => {
    if (!uploadFile) { setUploadMsg('Seleccioná un archivo CSV'); return; }
    const p = uploadPeriodo || periodo;
    if (!/^\d{4}-\d{2}$/.test(p)) { setUploadMsg('El período debe ser YYYY-MM'); return; }
    setUploading(true);
    setUploadMsg('');
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await fetch(
        `${API}/iva/import/${uploadLado}?periodo=${p}&reemplazar=true`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message ?? 'Error al importar');
      setUploadMsg(`✅ ${data.insertados} comprobantes importados`);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (p === periodo) fetchPosicion(periodo);
    } catch (e: any) {
      setUploadMsg(`❌ ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ----------------------------------------------------------------
  // Save manual
  // ----------------------------------------------------------------
  const handleSaveManual = async () => {
    if (!form.neto || isNaN(parseFloat(form.neto))) {
      setManualMsg('Ingresá un monto neto válido');
      return;
    }
    setSavingManual(true);
    setManualMsg('');
    try {
      const res = await fetch(`${API}/iva/manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          neto: parseFloat(form.neto),
          alicuota: parseFloat(form.alicuota),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message ?? 'Error al guardar');
      setManualMsg('✅ Comprobante cargado');
      setForm(defaultForm(periodo));
      if (form.periodo === periodo) fetchPosicion(periodo);
    } catch (e: any) {
      setManualMsg(`❌ ${e.message}`);
    } finally {
      setSavingManual(false);
    }
  };

  // ----------------------------------------------------------------
  // UI helpers
  // ----------------------------------------------------------------
  const kpiColor = (tipo: string) =>
    tipo === 'a_pagar' ? 'text-red-600' : tipo === 'a_favor' ? 'text-emerald-600' : 'text-slate-700';
  const kpiLabel = (tipo: string) =>
    tipo === 'a_pagar' ? 'A Pagar' : tipo === 'a_favor' ? 'A Favor' : 'Equilibrio';

  const posNecesario = posicion ? Math.max(0, posicion.posicion) : 0;
  const netoNecesario = posNecesario / 0.21;

  // ================================================================
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Posición IVA</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Débito fiscal vs crédito fiscal — ARCA / AFIP
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-600">Período</label>
          <select
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
          >
            {(posicion?.periodos ?? [periodo]).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <button
            onClick={() => fetchPosicion(periodo)}
            className="bg-brand-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand-700 transition-colors"
          >
            Actualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* KPI cards */}
      {loading && !posicion && (
        <div className="text-center py-12 text-slate-400 text-sm">Cargando…</div>
      )}
      {posicion && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">IVA Débito Fiscal</p>
            <p className="font-mono text-2xl font-bold text-slate-800 mt-1">
              {fmt(posicion.debito.totalIva)}
            </p>
            <p className="text-xs text-slate-400 mt-1">{posicion.debito.count.toLocaleString('es-AR')} comprobantes emitidos</p>
            <div className="flex gap-3 mt-1">
              <span className="text-xs text-slate-400">21%: {fmt(posicion.debito.iva21)}</span>
              <span className="text-xs text-slate-400">10,5%: {fmt(posicion.debito.iva105)}</span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">IVA Crédito Fiscal</p>
            <p className="font-mono text-2xl font-bold text-emerald-600 mt-1">
              {fmt(posicion.credito.totalIva)}
            </p>
            <p className="text-xs text-slate-400 mt-1">{posicion.credito.count.toLocaleString('es-AR')} comprobantes recibidos</p>
            <div className="flex gap-3 mt-1">
              <span className="text-xs text-slate-400">21%: {fmt(posicion.credito.iva21)}</span>
              <span className="text-xs text-slate-400">10,5%: {fmt(posicion.credito.iva105)}</span>
            </div>
          </div>

          <div className={`rounded-xl p-5 border ${posicion.tipo === 'a_pagar' ? 'bg-red-50 border-red-200' : posicion.tipo === 'a_favor' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
            <p className={`text-xs font-semibold uppercase tracking-widest ${posicion.tipo === 'a_pagar' ? 'text-red-500' : posicion.tipo === 'a_favor' ? 'text-emerald-600' : 'text-slate-400'}`}>
              Posición — {kpiLabel(posicion.tipo)}
            </p>
            <p className={`font-mono text-2xl font-bold mt-1 ${kpiColor(posicion.tipo)}`}>
              {fmtSigned(posicion.posicion)}
            </p>
            {posicion.tipo === 'a_pagar' && (
              <p className="text-xs text-slate-500 mt-1">
                Para llegar a cero necesitás {fmt(posNecesario)} más de crédito fiscal
                <span className="block text-slate-400">(≈ {fmt(netoNecesario)} neto de compras a 21%)</span>
              </p>
            )}
            {posicion.tipo === 'a_favor' && (
              <p className="text-xs text-slate-500 mt-1">
                Saldo a favor trasladable al próximo período
              </p>
            )}
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-slate-200">
          {[
            { id: 'posicion', label: '📊 Resumen' },
            { id: 'emitidos', label: '📤 Emitidos' },
            { id: 'recibidos', label: '📥 Recibidos' },
            { id: 'comprobantes', label: '➕ Cargar' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as any)}
              className={`px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === t.id
                  ? 'bg-brand-50 text-brand-700 border-b-2 border-brand-600'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ---- RESUMEN TAB ---- */}
        {activeTab === 'posicion' && posicion && (
          <div className="p-5 space-y-3">
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">Total facturado (con IVA)</span>
              <span className="font-mono font-semibold">{fmt(posicion.debito.total)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">Neto imponible emitido</span>
              <span className="font-mono font-semibold">{fmt(posicion.debito.neto)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">IVA Débito 21%</span>
              <span className="font-mono font-semibold text-red-600">{fmt(posicion.debito.iva21)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">IVA Débito 10,5%</span>
              <span className="font-mono font-semibold text-red-600">{fmt(posicion.debito.iva105)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">Total comprado (con IVA)</span>
              <span className="font-mono font-semibold">{fmt(posicion.credito.total)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">IVA Crédito 21%</span>
              <span className="font-mono font-semibold text-emerald-600">{fmt(posicion.credito.iva21)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 border-b border-slate-100">
              <span className="text-slate-500">IVA Crédito 10,5%</span>
              <span className="font-mono font-semibold text-emerald-600">{fmt(posicion.credito.iva105)}</span>
            </div>
            <div className="flex justify-between text-sm py-3 mt-2">
              <span className={`font-bold text-base ${posicion.tipo === 'a_pagar' ? 'text-red-600' : 'text-emerald-600'}`}>
                Posición neta
              </span>
              <span className={`font-mono font-bold text-lg ${posicion.tipo === 'a_pagar' ? 'text-red-600' : 'text-emerald-600'}`}>
                {fmtSigned(posicion.posicion)}
              </span>
            </div>
          </div>
        )}

        {/* ---- EMITIDOS TAB ---- */}
        {activeTab === 'emitidos' && posicion && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Comprobantes</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Neto</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">IVA 21%</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">IVA 10,5%</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Total IVA</th>
                </tr>
              </thead>
              <tbody>
                {posicion.emitidosPorFecha.map((r) => (
                  <tr key={r.fecha} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-600">{r.fecha}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{r.count}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-400">{fmt(r.neto)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-600">{r.iva21 ? fmt(r.iva21) : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-600">{r.iva105 ? fmt(r.iva105) : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-red-600">{fmt(r.totalIva)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-4 py-2.5 text-slate-700">Total</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{posicion.debito.count}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.debito.neto)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.debito.iva21)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.debito.iva105)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{fmt(posicion.debito.totalIva)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ---- RECIBIDOS TAB ---- */}
        {activeTab === 'recibidos' && posicion && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Emisor</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Comprobantes</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Total comprado</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">IVA 21%</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">IVA 10,5%</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Total IVA Crédito</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Última</th>
                </tr>
              </thead>
              <tbody>
                {posicion.recibidosPorEmisor.map((r) => (
                  <tr key={r.emisor} className={`border-b border-slate-100 hover:bg-slate-50 ${r.totalIva < 0 ? 'bg-red-50/40' : ''}`}>
                    <td className="px-4 py-2.5 text-slate-700 max-w-[260px] truncate">
                      {r.emisor}
                      {r.totalIva < 0 && <span className="ml-1.5 text-xs bg-red-100 text-red-600 border border-red-200 rounded px-1.5">NC</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{r.count}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-400">{fmtSigned(r.total)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-600">{r.iva21 ? fmtSigned(r.iva21) : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-600">{r.iva105 ? fmtSigned(r.iva105) : '—'}</td>
                    <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.totalIva < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {fmtSigned(r.totalIva)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-400 text-xs">{r.ultimaFecha}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-4 py-2.5 text-slate-700">Total</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{posicion.credito.count}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.credito.total)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.credito.iva21)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmt(posicion.credito.iva105)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{fmt(posicion.credito.totalIva)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ---- CARGAR TAB ---- */}
        {activeTab === 'comprobantes' && (
          <div className="p-5 space-y-8">
            {/* --- Import CSV --- */}
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-3">📂 Importar CSV de ARCA</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Tipo de comprobante
                  </label>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    value={uploadLado}
                    onChange={(e) => setUploadLado(e.target.value as any)}
                  >
                    <option value="emitido">Comprobantes Emitidos (Débito fiscal)</option>
                    <option value="recibido">Comprobantes Recibidos (Crédito fiscal)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Período (YYYY-MM) — se reemplazarán los CSV previos
                  </label>
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder={periodo}
                    value={uploadPeriodo}
                    onChange={(e) => setUploadPeriodo(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile}
                  className="bg-brand-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {uploading ? 'Importando…' : 'Importar CSV'}
                </button>
              </div>
              {uploadMsg && (
                <p className={`text-sm mt-2 ${uploadMsg.startsWith('✅') ? 'text-emerald-700' : 'text-red-600'}`}>
                  {uploadMsg}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Descargá el CSV desde ARCA → Consultas → Comprobantes emitidos/recibidos → Exportar CSV.
                El archivo debe estar en formato ARCA (separado por punto y coma, UTF-8).
              </p>
            </div>

            <hr className="border-slate-200" />

            {/* --- Manual entry --- */}
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-3">✏️ Cargar comprobante manualmente</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Tipo</label>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    value={form.lado}
                    onChange={(e) => setForm({ ...form, lado: e.target.value as any })}
                  >
                    <option value="emitido">Débito Fiscal (emitido)</option>
                    <option value="recibido">Crédito Fiscal (recibido)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Letra</label>
                  <div className="flex gap-1">
                    {(['A', 'B', 'C', 'M'] as const).map((l) => (
                      <button
                        key={l}
                        onClick={() => setForm({ ...form, tipoLetra: l })}
                        className={`flex-1 py-2 text-sm font-bold rounded-lg border transition-colors ${
                          form.tipoLetra === l
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-brand-400'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">¿Es Nota de Crédito?</label>
                  <button
                    onClick={() => setForm({ ...form, esNc: !form.esNc })}
                    className={`w-full py-2 text-sm font-semibold rounded-lg border transition-colors ${
                      form.esNc
                        ? 'bg-red-50 text-red-700 border-red-300'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {form.esNc ? '✓ Nota de Crédito' : 'No (Factura / ND)'}
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Fecha</label>
                  <input
                    type="date"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Período</label>
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="2026-08"
                    value={form.periodo}
                    onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Alícuota IVA</label>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    value={form.alicuota}
                    onChange={(e) => setForm({ ...form, alicuota: e.target.value })}
                  >
                    <option value="0.21">21%</option>
                    <option value="0.105">10,5%</option>
                    <option value="0.05">5%</option>
                    <option value="0.025">2,5%</option>
                    <option value="0">Exento</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Denominación (opcional)</label>
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Nombre del emisor o receptor"
                    value={form.denominacion}
                    onChange={(e) => setForm({ ...form, denominacion: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Monto Neto</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Ej: 500000"
                    value={form.neto}
                    onChange={(e) => setForm({ ...form, neto: e.target.value })}
                  />
                  {form.neto && !isNaN(parseFloat(form.neto)) && parseFloat(form.alicuota) > 0 && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      IVA: {fmt(parseFloat(form.neto) * parseFloat(form.alicuota))} · Total: {fmt(parseFloat(form.neto) * (1 + parseFloat(form.alicuota)))}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Notas (opcional)</label>
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Referencia, N° de comprobante, etc."
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveManual}
                  disabled={savingManual}
                  className="bg-brand-600 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingManual ? 'Guardando…' : 'Guardar comprobante'}
                </button>
                {manualMsg && (
                  <p className={`text-sm ${manualMsg.startsWith('✅') ? 'text-emerald-700' : 'text-red-600'}`}>
                    {manualMsg}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
